import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-slate-800/50 backdrop-blur-md border-b border-slate-700"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <Link
              to="/"
              className="flex items-center space-x-2 text-slate-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Sera</span>
            </Link>
          </div>
        </div>
      </motion.header>

      {/* Content */}
      <motion.main
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
      >
        <div className="prose prose-invert max-w-none">
          <h1 className="text-4xl font-bold mb-8 bg-gradient-to-r from-teal-400 to-blue-500 bg-clip-text text-transparent">
            Privacy Policy
          </h1>
          
          <p className="text-slate-400 text-lg mb-8">
            Last updated: [Date]
          </p>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Information We Collect</h2>
              <p className="text-slate-300 leading-relaxed">
                Sera is designed with privacy in mind. We collect minimal information necessary to provide our notification batching service:
              </p>
              <ul className="list-disc list-inside text-slate-300 mt-4 space-y-2">
                <li>App usage data to improve our batching algorithms</li>
                <li>Device information required for notification management</li>
                <li>User preferences for notification scheduling</li>
                <li>Anonymous analytics to enhance app performance</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. How We Use Your Information</h2>
              <p className="text-slate-300 leading-relaxed">
                Your data is used exclusively to:
              </p>
              <ul className="list-disc list-inside text-slate-300 mt-4 space-y-2">
                <li>Provide and improve our notification batching service</li>
                <li>Customize your notification preferences</li>
                <li>Ensure app stability and performance</li>
                <li>Send important app updates (with your consent)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Data Security</h2>
              <p className="text-slate-300 leading-relaxed">
                We implement industry-standard security measures to protect your information:
              </p>
              <ul className="list-disc list-inside text-slate-300 mt-4 space-y-2">
                <li>End-to-end encryption for sensitive data</li>
                <li>Local data processing whenever possible</li>
                <li>Regular security audits and updates</li>
                <li>Minimal data collection and retention policies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Third-Party Services</h2>
              <p className="text-slate-300 leading-relaxed">
                Sera may integrate with third-party services for analytics and app functionality. 
                These services are carefully selected for their privacy standards and data protection practices.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Your Rights</h2>
              <p className="text-slate-300 leading-relaxed">
                You have the right to:
              </p>
              <ul className="list-disc list-inside text-slate-300 mt-4 space-y-2">
                <li>Access your personal data</li>
                <li>Request data deletion</li>
                <li>Opt-out of data collection</li>
                <li>Export your data</li>
                <li>Contact us with privacy concerns</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Contact Us</h2>
              <p className="text-slate-300 leading-relaxed">
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-slate-800 rounded-lg">
                <p className="text-slate-300">
                  Email: privacy@sera.app<br />
                  Website: https://sera.app/contact
                </p>
              </div>
            </section>
          </div>

          <div className="mt-12 p-6 bg-gradient-to-r from-teal-400/10 to-blue-500/10 rounded-xl border border-teal-400/20">
            <p className="text-slate-300 text-center">
              <strong className="text-white">Privacy First:</strong> Sera is built with privacy as a core principle. 
              We believe you should have control over your notifications without compromising your personal data.
            </p>
          </div>
        </div>
      </motion.main>
    </div>
  );
};

export default PrivacyPolicy;
